// src/components/CheckinStudentDetail.jsx
//
// Full detail view for one student's check-in record. The check-in analogue of
// CheckoutStudentDetail. Subscribes to the record in real time, lists every
// item that APPLIES to the student (by audience), grouped into Required and
// Optional, and shows Clear / Undo / Mark-N/A actions gated by the user's
// per-item permissions.
//
// Props:
//   recordId: Firestore doc id (checkinRecords)
//   user: current auth user (incl. isSuperAdmin, uid, staffSisId)
//   resolvedItems: resolved check-in item catalog (with signers/audience/required)
//   onBack: return-to-dashboard callback

import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  ITEM_STATUS,
  RECORD_STATUS,
  KA_COLORS,
  getRelevantCheckinItems,
  computeCheckinCompletion,
  getItemStatus,
  getStatusLabel,
  getStatusColor,
  canCheckInItem,
  formatSignoffTimestamp,
} from '../utils/checkinHelpers';
import CheckinSignoffModal from './CheckinSignoffModal';

const styles = {
  page: {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    color: '#222', maxWidth: 1100, margin: '0 auto', padding: '16px 20px',
  },
  topBar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  backBtn: {
    background: 'none', border: `1px solid ${KA_COLORS.borderGray}`,
    padding: '6px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: '#333',
  },
  spacer: { flex: 1 },
  headerCard: {
    background: '#fff', border: `1px solid ${KA_COLORS.borderGray}`,
    borderTop: `4px solid ${KA_COLORS.red}`, borderRadius: 6,
    padding: 20, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 8,
  },
  studentName: { fontSize: 22, fontWeight: 700, color: KA_COLORS.navy, margin: 0 },
  studentMeta: { fontSize: 14, color: '#555', margin: 0 },
  metaRow: {
    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: 4, fontSize: 13,
  },
  tagPill: {
    background: KA_COLORS.navy, color: '#fff',
    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
  },
  progressOuter: {
    flex: 1, height: 10, background: KA_COLORS.borderGray,
    borderRadius: 5, overflow: 'hidden', minWidth: 200,
  },
  progressInner: (pct, complete) => ({
    width: `${pct}%`, height: '100%',
    background: complete ? '#2E7D32' : KA_COLORS.gold, transition: 'width 0.3s',
  }),
  progressLabel: { fontSize: 14, fontWeight: 700, color: KA_COLORS.navy, minWidth: 110, textAlign: 'right' },
  groupHeader: {
    fontSize: 12, fontWeight: 700, color: KA_COLORS.gold,
    textTransform: 'uppercase', letterSpacing: 0.6, margin: '24px 0 8px',
  },
  itemsList: { display: 'flex', flexDirection: 'column', gap: 10 },
  itemCard: {
    background: '#fff', border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 6, padding: 14,
  },
  itemRow: { display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' },
  itemMain: { flex: 1, minWidth: 240 },
  itemLabel: { fontSize: 15, fontWeight: 600, color: KA_COLORS.navy, margin: 0 },
  itemDesc: { fontSize: 12, color: '#666', margin: '4px 0 0', lineHeight: 1.4 },
  deptLine: { fontSize: 12, color: '#888', margin: '6px 0 0' },
  signoffInfo: { fontSize: 12, color: '#444', marginTop: 8, lineHeight: 1.5 },
  notesBox: {
    marginTop: 6, padding: '6px 8px', background: KA_COLORS.lightGray,
    borderRadius: 3, border: `1px solid ${KA_COLORS.borderGray}`,
    fontSize: 12, color: '#444', fontStyle: 'italic',
  },
  rightCol: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, minWidth: 130,
  },
  statusPill: (status) => {
    const c = getStatusColor(status);
    return {
      display: 'inline-block', padding: '4px 10px', borderRadius: 12,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
    };
  },
  actionsRow: { display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' },
  actionBtn: (variant) => {
    const palette = {
      primary: { bg: KA_COLORS.red, fg: '#fff', border: KA_COLORS.red },
      secondary: { bg: '#fff', fg: KA_COLORS.navy, border: KA_COLORS.borderGray },
      muted: { bg: '#fff', fg: '#777', border: KA_COLORS.borderGray },
    };
    const p = palette[variant] || palette.secondary;
    return {
      padding: '5px 10px', border: `1px solid ${p.border}`, background: p.bg,
      color: p.fg, borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
    };
  },
  unauthorizedNote: { fontSize: 11, color: '#999', fontStyle: 'italic', marginTop: 4, textAlign: 'right' },
  loadingBox: { padding: 40, textAlign: 'center', color: '#666', fontStyle: 'italic' },
  errorBox: {
    padding: 16, background: '#FFEBEE', border: '1px solid #FFCDD2',
    borderRadius: 4, color: '#C62828', fontSize: 13, margin: '20px 0',
  },
};

const ItemCard = ({ item, data, user, onSign, onUndo, onMarkNa }) => {
  const status = data?.status || ITEM_STATUS.PENDING;
  const canSign = canCheckInItem(user, item);
  const isSuperAdmin = !!user?.isSuperAdmin;

  let actions = null;
  if (status === ITEM_STATUS.PENDING) {
    actions = canSign ? (
      <div style={styles.actionsRow}>
        <button type="button" style={styles.actionBtn('primary')} onClick={() => onSign(item.id)}>
          Clear
        </button>
        {isSuperAdmin && (
          <button type="button" style={styles.actionBtn('muted')} onClick={() => onMarkNa(item.id)}>
            Mark N/A
          </button>
        )}
      </div>
    ) : (
      <span style={styles.unauthorizedNote}>Not authorized for this item</span>
    );
  } else if (status === ITEM_STATUS.SIGNED) {
    actions = canSign ? (
      <div style={styles.actionsRow}>
        <button type="button" style={styles.actionBtn('secondary')} onClick={() => onUndo(item.id)}>
          Undo
        </button>
      </div>
    ) : null;
  } else if (status === ITEM_STATUS.NA) {
    actions = isSuperAdmin ? (
      <div style={styles.actionsRow}>
        <button type="button" style={styles.actionBtn('secondary')} onClick={() => onSign(item.id)}>
          Reactivate &amp; clear
        </button>
      </div>
    ) : null;
  }

  return (
    <div style={styles.itemCard}>
      <div style={styles.itemRow}>
        <div style={styles.itemMain}>
          <p style={styles.itemLabel}>{item.label}</p>
          {item.description && <p style={styles.itemDesc}>{item.description}</p>}
          {item.department && (
            <p style={styles.deptLine}>Contact: <strong>{item.department}</strong></p>
          )}
          {status === ITEM_STATUS.SIGNED && (
            <div style={styles.signoffInfo}>
              Cleared by{' '}
              <strong style={{ color: KA_COLORS.navy }}>{data.signedByName || 'Unknown'}</strong>
              {data.signedAt && <> &nbsp;•&nbsp; {formatSignoffTimestamp(data.signedAt)}</>}
              {data.notes && data.notes.trim().length > 0 && (
                <div style={styles.notesBox}>
                  <strong>Notes:</strong> {data.notes}
                </div>
              )}
            </div>
          )}
        </div>
        <div style={styles.rightCol}>
          <span style={styles.statusPill(status)}>{getStatusLabel(status)}</span>
          {actions}
        </div>
      </div>
    </div>
  );
};

const CheckinStudentDetail = ({ recordId, user, resolvedItems, onBack }) => {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signoffModal, setSignoffModal] = useState(null); // { itemId, mode }

  useEffect(() => {
    if (!recordId) return undefined;
    const ref = doc(db, 'checkinRecords', recordId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setRecord(null);
          setError('Check-in record not found.');
          setLoading(false);
          return;
        }
        setRecord({ id: snap.id, ...snap.data() });
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load check-in record:', err);
        setError(err.message || 'Failed to load record.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [recordId]);

  const relevant = useMemo(
    () => (record ? getRelevantCheckinItems(record, resolvedItems) : []),
    [record, resolvedItems],
  );
  const requiredItems = useMemo(() => relevant.filter((it) => it.required), [relevant]);
  const optionalItems = useMemo(() => relevant.filter((it) => !it.required), [relevant]);
  const completion = useMemo(
    () => computeCheckinCompletion(record, resolvedItems),
    [record, resolvedItems],
  );

  const openSign = (itemId) => setSignoffModal({ itemId, mode: 'sign' });
  const openUndo = (itemId) => setSignoffModal({ itemId, mode: 'undo' });
  const openMarkNa = (itemId) => setSignoffModal({ itemId, mode: 'na' });
  const closeModal = () => setSignoffModal(null);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingBox}>Loading check-in record…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.topBar}>
          <button type="button" style={styles.backBtn} onClick={onBack}>← Back</button>
        </div>
        <div style={styles.errorBox}>{error}</div>
      </div>
    );
  }
  if (!record) return null;

  const isArchived = record.status === RECORD_STATUS.ARCHIVED;
  const modalItem = signoffModal ? relevant.find((it) => it.id === signoffModal.itemId) : null;

  const renderGroup = (label, items) =>
    items.length > 0 && (
      <div>
        <h2 style={styles.groupHeader}>{label}</h2>
        <div style={styles.itemsList}>
          {items.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              data={record.items?.[item.id]}
              user={user}
              onSign={isArchived ? () => {} : openSign}
              onUndo={isArchived ? () => {} : openUndo}
              onMarkNa={isArchived ? () => {} : openMarkNa}
            />
          ))}
        </div>
      </div>
    );

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <button type="button" style={styles.backBtn} onClick={onBack}>← Back to dashboard</button>
        <div style={styles.spacer} />
      </div>

      {isArchived && (
        <div style={{ ...styles.errorBox, background: '#E3F2FD', border: '1px solid #BBDEFB', color: '#1565C0' }}>
          This record is from school year <strong>{record.schoolYear}</strong> and is archived. Read-only.
        </div>
      )}

      <div style={styles.headerCard}>
        <h1 style={styles.studentName}>{record.studentName || 'Unknown'}</h1>
        <p style={styles.studentMeta}>
          {[
            record.grade,
            record.cohort,
            record.studentId && `SIS ${record.studentId}`,
          ].filter(Boolean).join('  •  ')}
        </p>
        <div style={styles.metaRow}>
          {record.boarding ? (
            <span style={styles.tagPill}>Boarding</span>
          ) : (
            <span style={{ ...styles.tagPill, background: '#00796B' }}>Day</span>
          )}
          {record.isInternational && (
            <span style={{ ...styles.tagPill, background: KA_COLORS.gold }}>International</span>
          )}
          <span style={{ fontSize: 13, color: '#666' }}>
            School year: <strong>{record.schoolYear}</strong>
          </span>
        </div>
        <div style={{ ...styles.metaRow, marginTop: 12 }}>
          <div style={styles.progressOuter}>
            <div style={styles.progressInner(completion, completion === 100)} />
          </div>
          <div style={styles.progressLabel}>{completion}% required done</div>
        </div>
      </div>

      {renderGroup('Required', requiredItems)}
      {renderGroup('Optional', optionalItems)}

      {!isArchived && signoffModal && modalItem && (
        <CheckinSignoffModal
          record={record}
          item={modalItem}
          user={user}
          mode={signoffModal.mode}
          onClose={closeModal}
          onSuccess={closeModal}
        />
      )}
    </div>
  );
};

export default CheckinStudentDetail;